import React, { useState } from 'react';
import { X, File, AlertCircle } from 'lucide-react';
import { api } from '../utils/api';
import { useAuthStore } from '../stores/authStore';

interface CreateFileModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPath: string;
  onFileCreated?: (file: any) => void;
}

export const CreateFileModal: React.FC<CreateFileModalProps> = ({
  isOpen,
  onClose,
  currentPath,
  onFileCreated
}) => {
  const [filename, setFilename] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { token, csrfToken } = useAuthStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!filename.trim()) {
      setError('Filename is required');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await api.post<{ file: unknown }>('/api/files/create', {
        filename: filename.trim(),
        parentPath: currentPath,
        isPublic: false
      }, token || '', csrfToken || undefined);

      if (response.success) {
        // File created successfully - just refresh the file list
        // Don't auto-open editor to avoid timing issues
        onFileCreated?.(response.data?.file);
        onClose();
        setFilename('');
      } else {
        setError(response.error || 'Failed to create file');
      }
    } catch (error: any) {
      console.error('Create file error:', error);
      setError('Failed to create file');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFilename('');
    setError('');
    onClose();
  };

  const getFileTypeFromFilename = (filename: string) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'js': return 'JavaScript';
      case 'ts': return 'TypeScript';
      case 'html': return 'HTML';
      case 'css': return 'CSS';
      case 'php': return 'PHP';
      case 'py': return 'Python';
      case 'json': return 'JSON';
      case 'md': return 'Markdown';
      case 'txt': return 'Text';
      default: return 'File';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <File className="h-5 w-5 text-blue-400" />
            <div>
              <h2 className="text-lg font-semibold text-white">Create New File</h2>
              <p className="text-sm text-gray-400">
                Create in: {currentPath || 'Root folder'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              File Name
            </label>
            <div className="relative">
              <File className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={filename}
                onChange={(e) => setFilename(e.target.value)}
                placeholder="script.js"
                className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoFocus
                disabled={isLoading}
              />
            </div>
            {filename && (
              <div className="mt-2 text-sm text-gray-400">
                File type: {getFileTypeFromFilename(filename)} • Path: {currentPath ? `${currentPath}/${filename}` : filename}
              </div>
            )}
            {error && (
              <div className="mt-2 flex items-center gap-2 text-red-400 text-sm">
                <AlertCircle className="h-4 w-4" />
                {error}
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 text-gray-300 border border-gray-600 rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              disabled={isLoading || !filename.trim()}
            >
              <File className="h-4 w-4" />
              {isLoading ? 'Creating...' : 'Create File'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};